import {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useContext,
} from "react";
import { Typography, TextField, Button, CircularProgress } from "@mui/material";
import EmailEditor from "react-email-editor";
import SchedulerDialog from "./SchedulerDialog";
import { MessengerContext } from "../../../contexts/MessengerContext";

const EmailTemplate = forwardRef(
  ({ template, update, pairingEvent, leagueEvent, send, customSender }, ref) => {
    const emailEditorRef = useRef(null);
    const { canSend, templateId } = useContext(MessengerContext);
    const [templateData, setTemplateData] = useState({
      name: "",
      subject: "",
      htmlBody: "",
      jsonBody: "",
    });
    const [schedulerOpen, setSchedulerOpen] = useState(false);
    const [sending, setSending] = useState(false);

    useEffect(() => {
      if (templateId === "custom") {
        handleReset();
        return;
      }
      if (template) {
        setTemplateData({ ...template });
      }
    }, [templateId, template]);

    const onReady = (unlayer) => {
      if (templateData?.jsonBody) {
        unlayer.loadDesign(templateData.jsonBody);
      }
    };

    // New effect: load design when templateData changes
    useEffect(() => {
      const unlayer = emailEditorRef.current?.editor;
      if (unlayer && templateData?.jsonBody) {
        unlayer.loadDesign(templateData.jsonBody);
      }
    }, [templateData]);

    const renderButtons = () => {
      if (pairingEvent || leagueEvent) {
        return (
          <div style={{ width: "100%" }}>
            <Button
              variant="contained"
              onClick={handleSend}
              style={{ marginTop: 30, width: "100%" }}
              disabled={templateData?.subject && canSend ? false : true}
            >
              {sending ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : (
                "send"
              )}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setSchedulerOpen(true)}
              style={{ marginTop: 30, width: "100%" }}
              disabled={templateData?.subject && canSend ? false : true}
            >
              send later
            </Button>
            <div style={{ height: 100 }} />
          </div>
        );
      } else if (customSender) {
        return (
          <div style={{ width: "100%" }}>
            <Button
              variant="contained"
              onClick={handleCustomSend}
              style={{ marginTop: 30, width: "100%" }}
            >
              send now
            </Button>
            {/* <Button
              variant="outlined"
              onClick={() => setSchedulerOpen(true)}
              style={{ marginTop: 30, width: "100%" }}
              disabled={true}
            >
              send later
            </Button> */}
            <div style={{ height: 100 }} />
          </div>
        );
      } else {
        return (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "16px" }}
          >
            <Button
              variant="contained"
              onClick={() => handleSave(null)}
              style={{ marginTop: 30 }}
              disabled={templateData?.subject ? false : true}
            >
              save
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleSave(null, true)}
              disabled={templateData?.subject ? false : true}
            >
              save as
            </Button>
          </div>
        );
      }
    };

    const renderVariableMsg = () => {
      if (!canSend) {
        return null;
      }
      if (leagueEvent) {
        return (
          <div
            style={{
              width: "100%",
              padding: 12,
              backgroundColor: "#f2f2f2",
              borderRadius: 4,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
              Available variables (click to copy):
            </Typography>
            {[
              { key: "firstName", description: "The attendee's first name" },
              { key: "eventName", description: "The name of the event" },
              { key: "eventDate", description: "The date of the event" },
              { key: "venueName", description: "Name of the venue" },
              { key: "venueAddress", description: "Address of the venue" },
              { key: "leagueStandings", description: "Current standings table — renders as HTML table" },
            ].map((v) => (
              <div
                key={v.key}
                style={{ display: "flex", justifyContent: "space-between", fontSize: 13, cursor: "pointer" }}
                onClick={() => {
                  navigator.clipboard.writeText(`{{${v.key}}}`);
                }}
              >
                <div>
                  <span style={{ fontFamily: "monospace" }}>{`{{${v.key}}}`}</span>
                  <span style={{ marginLeft: 8, color: "#666" }}>— {v.description}</span>
                </div>
                <span style={{ color: "#999", fontSize: 12 }}>Click to copy</span>
              </div>
            ))}
          </div>
        );
      }
      if (!pairingEvent && !templateId) {
        return null;
      } else if (pairingEvent) {
        return (
          <div
            style={{
              width: "100%",
              padding: 12,
              backgroundColor: "#f2f2f2",
              borderRadius: 4,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
              Available variables (click to copy):
            </Typography>

            {[
              { key: "firstName", description: "The attendee’s first name" },
              { key: "eventName", description: "The name of the event" },
              {
                key: "eventDate",
                description: "The date of the event",
              },
              {
                key: "questionGroup",
                description:
                  "The name of the question group assigned to the event",
              },
              {
                key: "{siteUrl}",
                description:
                  "Link to the user site (i.e. where they create their accounts)",
              },
              { key: "{noShowUrl}", description: "Link to No-Show form" },
              {
                key: "{dinnerCheckInUrl}",
                description: "Link to page to check into dinner events",
              },
              {
                key: "tableNumber",
                description: "Table number of attendee for dinner events",
              },
              {
                key: "venueName",
                description: "Name of the venue for the event",
              },
              {
                key: "venueAddress",
                description: "Address of the venue for the event",
              },
            ].map((v) => (
              <div
                key={v.key}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 13,
                  cursor: "pointer",
                }}
                onClick={() => {
                  navigator.clipboard.writeText(`{{${v.key}}}`);
                }}
              >
                <div>
                  <span
                    style={{ fontFamily: "monospace" }}
                  >{`{{${v.key}}}`}</span>
                  <span style={{ marginLeft: 8, color: "#666" }}>
                    — {v.description}
                  </span>
                </div>
                <span style={{ color: "#999", fontSize: 12 }}>
                  Click to copy
                </span>
              </div>
            ))}
          </div>
        );
      } else if (!pairingEvent && templateId) {
        return (
          <Typography
            variant="subtitle2"
            style={{ width: "100%", padding: 8, backgroundColor: "#dddddd" }}
          >
            The available variables for this template are: {template?.variables}
          </Typography>
        );
      } else {
        return null;
      }
    };

    const handleSave = (name, saveAs = false) => {
      const unlayer = emailEditorRef.current?.editor;

      if (unlayer) {
        unlayer.exportHtml(function (d) {
          const data = {
            subject: templateData?.subject,
            htmlBody: d.html,
            jsonBody: d.design,
          };
          if (!saveAs) {
            data._id = template?._id;
          }
          if (name) {
            data.name = name;
          }
          update(data, saveAs);
        });
      } else {
        console.warn("Unlayer editor not ready");
      }
    };

    const handleSend = async () => {
      setSending(true);
      const unlayer = emailEditorRef.current?.editor;

      if (unlayer) {
        unlayer.exportHtml(async function (d) {
          const data = {
            subject: templateData?.subject,
            htmlBody: d.html,
            jsonBody: d.design,
          };

          await send(data);
          setSending(false);
        });
      } else {
        console.warn("Unlayer editor not ready");
      }
    };

    const handleCustomSend = () => {
      const unlayer = emailEditorRef.current?.editor;

      if (unlayer) {
        unlayer.exportHtml(function (d) {
          const data = {
            subject: templateData?.subject,
            htmlBody: d.html,
            jsonBody: d.design,
          };

          customSender(data);
        });
      } else {
        console.warn("Unlayer editor not ready");
      }
    };

    const handleReset = () => {
      const unlayer = emailEditorRef.current?.editor;

      if (unlayer) {
        unlayer.loadDesign({
          body: {
            rows: [],
            values: {},
          },
        }); // Clear the editor
      }
      setTemplateData({
        name: "",
        subject: "",
        htmlBody: "",
        jsonBody: "",
      });
    };

    useImperativeHandle(ref, () => ({
      handleSave,
      handleReset,
    }));

    return (
      <div
        style={{
          height: "85%",
          display: "flex",
          flexDirection: "column",
          padding: 10,
          gap: 20,
        }}
      >
        {templateId && templateId !== "custom" && (pairingEvent || leagueEvent) ? (
          <Typography variant="subtitle2" align="center">
            Any changes you make to this template will be sent to the selected
            recipients. However, the the changes will not be saved in the
            database
          </Typography>
        ) : null}
        {renderVariableMsg()}

        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 10,
            width: "70%",
            paddingLeft: "30px",
          }}
        >
          <Typography style={{ fontWeight: "bold" }}>Subject: </Typography>
          <TextField
            value={templateData?.subject || ""}
            onChange={(e) =>
              setTemplateData({ ...template, subject: e.target.value })
            }
            size="small"
            fullWidth
          />
        </div>
        <EmailEditor
          ref={emailEditorRef}
          onReady={onReady}
          options={{
            id: "editor-container",
            displayMode: "email",
            features: {
              preview: false,
            },
          }}
        />
        <SchedulerDialog
          open={schedulerOpen}
          close={() => setSchedulerOpen(false)}
          handleSend={handleSend}
        />

        {renderButtons()}
      </div>
    );
  }
);

export default EmailTemplate;
